import { useEffect, useState } from 'react'
import { DashboardMode } from '@phrasee/phrasee-typings/Graphql/interfaces'

import { createCancelTokenSource } from 'common/api'
import { ReportUpliftLeveledItem } from 'common/api/reporting'
import WidgetLoader from 'common/components/loaders/WidgetLoader'
import Widget from 'common/components/Widget'
import WidgetHeader, { InfoAction } from 'common/components/WidgetHeader'
import { WidgetIncrementalError } from 'common/components/WidgetMessage'
import { useAppSelector } from 'common/hooks/redux'
import { fetchCampaigns } from 'features/campaigns/api'

import {
  ProjectSummaryResponse,
  useActiveProjects,
  useGetBroadcastMetricsQuery,
} from '../../api'
import { PerformanceIndicator } from '../components/PerformanceIndicator'

interface Props {
  accountId: string
}

const calculateBroadcastMetric = (
  data: ProjectSummaryResponse,
  metricName: string
) => {
  const results = data?.email_projects?.reduce((acc, currentValue) => {
    if (currentValue[metricName]) {
      acc += currentValue[metricName]
    }
    return acc
  }, 0)
  return results
}

const BroadcastKPI = ({ accountId }: Props) => {
  const { dashboardMode } = useAppSelector((state) => state.authStates)
  const source = createCancelTokenSource()

  const { data: activeProjectsData, hasGQLError: activeProjectsError } =
    useActiveProjects(accountId, source, 'broadcast')

  const activeBroadcastProjectIds = activeProjectsData ?? []

  const { data: queryData, isError: getMetricsError } =
    useGetBroadcastMetricsQuery(
      accountId,
      dashboardMode,
      activeBroadcastProjectIds
    )

  const [results, setResults] = useState({
    totalIncrementalOpens: 0,
    totalIncrementalClicks: 0,
    totalBroadcastCampaigns: 0,
    averageOpensUplift: 0,
    averageClicksUplift: 0,
  })

  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (queryData && activeBroadcastProjectIds.length > 0) {
      const data = queryData.data.data
      if (!isLoading) {
        setIsLoading(true)
      }
      if ('email_projects' in data && Object.keys(data).length > 0) {
        const newResults = {
          ...results,
          totalIncrementalOpens: calculateBroadcastMetric(
            data,
            'incremental_opens'
          ),
          totalIncrementalClicks: calculateBroadcastMetric(
            data,
            'incremental_clicks'
          ),
          totalBroadcastCampaigns: calculateBroadcastMetric(
            data,
            'number_of_campaigns'
          ),
        }
        setResults(newResults)
        setIsLoading(false)
      } else if (Array.isArray(data)) {
        fetchCampaigns({
          variables: {
            page: 1,
            pageSize: 10,
            filter: {
              accountId,
              projectsList: activeBroadcastProjectIds,
            },
          },
          source,
        })
          .then((res) => {
            const foundEntry = (data as ReportUpliftLeveledItem[]).find(
              (entry) => entry.period_label
            )
            if (foundEntry) {
              const upliftsObject = foundEntry.values.find(
                (value) => value.name === 'Average'
              )

              if (upliftsObject) {
                const emailOpens = upliftsObject.values.find(
                  (value) => value.label === 'Email Opens'
                )
                const emailClicks = upliftsObject.values.find(
                  (value) => value.label === 'Email Clicks'
                )
                const newResults = {
                  ...results,
                  averageOpensUplift: emailOpens?.value || 0,
                  averageClicksUplift: emailClicks?.value || 0,
                  totalBroadcastCampaigns: res.count,
                }
                setResults(newResults)
                setIsLoading(false)
              }
            }
            setIsLoading(false)
          })
          .catch(() => {
            setIsLoading(false)
          })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData])

  const hasToolTip = !isLoading && results.totalBroadcastCampaigns !== 0
  const hasMetrics = !isLoading && !getMetricsError
  const hasError = activeProjectsError || getMetricsError

  const hasNoData = (dashboardMode: DashboardMode | undefined): boolean => {
    if (!results) {
      return true
    }

    return (
      (dashboardMode === 'Incremental' &&
        results.totalIncrementalClicks === 0 &&
        results.totalIncrementalOpens === 0) ||
      (dashboardMode === 'Average_uplift' &&
        results.averageClicksUplift === 0 &&
        results.averageOpensUplift === 0)
    )
  }

  return (
    <Widget className="max-h-full">
      <WidgetHeader
        data-cy="broadcast-KPI-Widget"
        className="text-coolGray-800"
        title={
          hasError ? (
            <span data-cy="number-of-broadcast-experiments-widget-title">
              Broadcast
            </span>
          ) : (
            <span data-cy="number-of-broadcast-experiments-widget-title">
              {`${
                results.totalBroadcastCampaigns === 0
                  ? ''
                  : results.totalBroadcastCampaigns
              } `}
              Broadcast
            </span>
          )
        }
        actions={
          hasToolTip && (
            <InfoAction
              message={
                dashboardMode === 'Incremental'
                  ? 'This widget displays your total incremental opens and clicks for all active Broadcast email projects  for the past 12 months. If you want to dive deeper into your KPIs, head to the Reports page.'
                  : 'This widget displays your average uplift for opens and clicks for all active Broadcast email projects for the past 12 months. If you want to dive deeper into your KPIs, head to the Reports page.'
              }
            />
          )
        }
      />
      {isLoading && !hasError && (
        <WidgetLoader
          data-cy="number-of-broadcast-experiments-widget-loader"
          size="small"
        />
      )}
      {hasError && <WidgetIncrementalError />}
      {hasMetrics && (
        <div>
          <div>
            {hasNoData(dashboardMode) && (
              <div className="display box-border text-sm p-4 gap-4 bg-gray-50 border-solid border border-gray-300 rounded-md text-center">
                Run your first <span className="font-bold"> "Experiment"</span>,
                then your data will start to appear here.
              </div>
            )}
          </div>
          <div className="flex flex-row gap-6 pt-3">
            <PerformanceIndicator
              className="flex flex-1 flex-col pt-10 border-r-1 border-dashed border-coolGray-300 pr-6"
              kpiTitle={
                dashboardMode === 'Incremental'
                  ? 'Incremental opens'
                  : 'Average uplift for opens'
              }
              kpiValue={
                dashboardMode === 'Incremental'
                  ? results.totalIncrementalOpens
                  : `${results.averageOpensUplift}%`
              }
            />
            <PerformanceIndicator
              className="flex flex-1 flex-col pt-10 max-h-screen"
              kpiTitle={
                dashboardMode === 'Incremental'
                  ? 'Incremental clicks'
                  : 'Average uplift for clicks'
              }
              kpiValue={
                dashboardMode === 'Incremental'
                  ? results.totalIncrementalClicks
                  : `${results.averageClicksUplift}%`
              }
            />
          </div>
        </div>
      )}
    </Widget>
  )
}

export default BroadcastKPI
